/**
 * Mobile App Event Schemas — the per-app append-only activity history.
 *
 * One event stream covers everything done to a white-label app: delivery
 * (builds, OTA updates, submissions, review outcomes, release results),
 * configuration edits, image/asset changes, onboarding and provisioning
 * steps, and push configuration changes.
 *
 * The event-type catalog below is a CLOSED UNION grouped by class. It is
 * closed on purpose: the fleet UI groups and filters the timeline by class
 * without string matching, and a typo in a call site becomes a compile
 * error rather than an orphan row nothing will ever query.
 *
 * `MOBILE_APP_EVENT_TYPES` maps every type to its class, so a recorder can
 * derive `eventClass` from `eventType` and the two can never disagree in the
 * database.
 *
 * Adding a type: add one entry to `MOBILE_APP_EVENT_TYPES`. Both columns are
 * `VARCHAR` in Prisma rather than DB enums precisely so growing this catalog
 * needs no PlanetScale promotion.
 *
 * Plan: docs/plans/fleet-app-delivery/b-event-spine-and-readiness.md
 * (U3; requirements R14–R16, R23)
 */

import { z } from 'zod'

// =============================================================================
// Event classes
// =============================================================================

export const MOBILE_APP_EVENT_CLASSES = ['delivery', 'configuration', 'asset', 'onboarding', 'push'] as const

export const MobileAppEventClassSchema = z.enum(MOBILE_APP_EVENT_CLASSES)
export type MobileAppEventClass = z.infer<typeof MobileAppEventClassSchema>

// =============================================================================
// Event-type catalog (closed union, grouped by class)
// =============================================================================

/**
 * Every recordable event type, mapped to the class it belongs to.
 *
 * Naming: `<subject>.<past-tense outcome>`. Outcome lives in the type
 * (`build.succeeded` vs `build.failed`) rather than in a separate column, so
 * a filter on "what failed for this app" is a prefix scan over one field.
 */
export const MOBILE_APP_EVENT_TYPES = {
  // --- delivery: builds, submissions, OTA updates, releases ---
  'release.created': 'delivery',
  'release.targets_added': 'delivery',
  'release.cancelled': 'delivery',
  'release.completed': 'delivery',
  'release.target_dispatched': 'delivery',
  'release.target_retried': 'delivery',
  'release.target_failed': 'delivery',
  'release.target_cancelled': 'delivery',
  'build.dispatched': 'delivery',
  'build.started': 'delivery',
  'build.succeeded': 'delivery',
  'build.failed': 'delivery',
  /**
   * A CI run snapshotted a school's registry record into the generated bundle
   * it is about to build or publish from (plan 2026-09-01-2310 KTD8, R20).
   *
   * Keyed on the workflow RUN id rather than on a release target, because a run
   * dispatched from the GitHub UI has no target and therefore no artifact to
   * carry its provenance — and a build whose provenance is unrecoverable is
   * exactly what R20 exists to prevent. When a target does exist the same
   * manifest also lands on `AppArtifact.payload`; this row is the floor, not a
   * duplicate of it.
   */
  'build.snapshot_recorded': 'delivery',
  'submission.submitted': 'delivery',
  'submission.review_started': 'delivery',
  'submission.approved': 'delivery',
  'submission.rejected': 'delivery',
  'submission.released': 'delivery',
  /**
   * An operator (or an agent acting for one) asked Apple to release an approved
   * version — our `POST /v1/appStoreVersionReleaseRequests` call, or the typed
   * refusal that stopped it before it was made (SCH-1319 R10, KTD5).
   *
   * Deliberately NOT folded into `submission.released`. They are two different
   * facts about two different actors: this one is US asking, written by the
   * release action; `submission.released` is APPLE confirming, written by the
   * ASC webhook lane. Folding them would double-count every release — one row
   * for the request and one for the store's own confirmation of the same
   * publish — and a second request for a version already requested is permitted
   * (R14), so the request side is not even one-per-release.
   */
  'submission.release_requested': 'delivery',
  /**
   * A live probe of what Apple says about an app's current App Store version —
   * the refresh route, and every one of its outcomes (SCH-1319 R10, KTD2).
   *
   * RECORDED ON EVERY ATTEMPT, including the ones that never reached a
   * determination. The probe writes no column and changes nothing, so without
   * this row an operator who re-checks and sees nothing move cannot tell a
   * failure from a no-op — the same reason `account.api_access_checked` is
   * written per attempt rather than per state change.
   *
   * `delivery`, not `configuration`: it is a provider reporting where a
   * submission stands, which is the evidence class `submission.*` occupies.
   * Severity carries the difference between a determination (`info`) and an
   * unreadable probe (`warn`); the class does not.
   */
  'submission.version_checked': 'delivery',
  /**
   * TestFlight external-build state, from `buildBetaDetailExternalBuildStateUpdated`.
   *
   * Two types, not one per Apple state: `testflight.ready_for_testing` is the
   * transition anyone actually waits on (the build reached external testers),
   * and everything else on that lane — beta review submitted, approved,
   * rejected, expired — lands on `testflight.state_changed` with Apple's own
   * state in the payload. Same reasoning as `delivery.apple_event_unhandled`:
   * Apple's enum is theirs to grow, and a member per state would make their
   * addition our shared-types edit.
   */
  'testflight.ready_for_testing': 'delivery',
  'testflight.state_changed': 'delivery',
  'ota.dispatched': 'delivery',
  'ota.published': 'delivery',
  'ota.failed': 'delivery',
  /**
   * An App Store Connect webhook event this platform has no processor for.
   *
   * Deliberately ONE type covering every unhandled Apple event rather than one
   * per Apple event type: Apple's catalog is theirs to grow, and minting a
   * union member per addition would make "Apple shipped a new event" a
   * shared-types edit — which is exactly the coupling recording these exists to
   * avoid. Apple's real `eventType` rides in the summary and payload.
   */
  'delivery.apple_event_unhandled': 'delivery',

  /**
   * VERSION LEDGER (plan 2026-09-02-2140). Seven types, one per way a
   * `MobileAppDeliveryState` fact group moves.
   *
   * The three `*_observed` types are `delivery`: a provider proved what was
   * built, published or served, which is exactly the evidence class a build or a
   * submission writes. They carry `dedupeOn: [platform, group, value]`, so a
   * sweep that observes an unchanged version records NOTHING — at a 6-hour
   * cadence over 100 apps, an event per tick would be 1,200 rows a day saying
   * the fleet did not move.
   */
  'delivery_state.build_observed': 'delivery',
  'delivery_state.ota_observed': 'delivery',
  'delivery_state.store_live_observed': 'delivery',
  /**
   * A person (or an agent through the internal endpoint) set or cleared a fact
   * group by hand. `configuration`, alongside `app.store_state_changed` and for
   * the same reason: a hand-typed version is a DECLARATION, and mixing it into
   * the delivery stream would make that stream unreliable as evidence of what a
   * provider actually reported.
   *
   * Never deduplicated: a re-submitted identical value is already a full no-op
   * at the writer (R11), so every event that reaches the recorder describes a
   * real move and must be recorded even when it restores a previous value.
   */
  'delivery_state.set_manually': 'configuration',
  'delivery_state.cleared_manually': 'configuration',
  /**
   * An observation displaced a manual value (R12). `delivery`, not
   * `configuration`: the actor is the sweep and the evidence is the provider's:
   * this is the record of the machine overruling the human, and the payload
   * names the displaced value and who had set it so the human can see why.
   */
  'delivery_state.manual_corrected': 'delivery',
  /**
   * One row per completed sweep, carrying the flagged app names and counts
   * (R22), `dedupeOn: [runDate, lanes]`. It exists so a flag that persists for
   * weeks is discoverable without scanning fleet-table cells.
   */
  'delivery_state.sweep_completed': 'delivery',
  /**
   * A super-admin pressed "Verify now" on the per-app page (R19) and a
   * single-app reconcile was queued. Recorded because the request stamps
   * `reconcileQueuedAt` on both platform rows — a write with no other history
   * entry — and because who asked for a re-check is part of the version story.
   * Not emitted for an `already_queued` answer: nothing changed.
   */
  'delivery_state.reconcile_requested': 'delivery',

  // --- configuration: registry edits and credentials ---
  'app.created': 'configuration',
  'app.updated': 'configuration',
  /**
   * A super-admin (or the internal principal) moved ONE store's listing state —
   * `iosStoreState` or `androidStoreState` (plan 2026-09-03-0330 R3).
   *
   * `configuration`, alongside `app.updated`, rather than `delivery`: this
   * records a person's DECLARATION about where the app stands, not an observed
   * delivery transition. The `delivery` class is what a build, a submission or a
   * store webhook writes, and mixing a hand-typed claim into that stream would
   * make the delivery timeline unreliable as evidence.
   *
   * One event per store per move, never a combined one — the payload carries
   * `{ platform, from, to, source, note }`, and a no-op write (same state, same
   * note) records nothing at all.
   */
  'app.store_state_changed': 'configuration',
  'app.credentials_updated': 'configuration',
  /**
   * A short-lived scoped token was minted from a vaulted provisioning credential
   * for an external consumer (credential vault R8/KTD7).
   *
   * Recorded only when the request named an app, because `MobileAppEvent`
   * requires a `mobileAppId` and an account-scoped issuance has no app
   * dimension; those are recorded as a stable log line instead. The payload
   * carries the caller identifier, the account, the key id and the scope — never
   * the token and never key material.
   */
  'app.credentials_issued': 'configuration',
  /**
   * An issuance was REFUSED — the account row is suspended (`isActive: false`),
   * which is the only revocation Apple allows short of destroying the key.
   *
   * A separate type rather than a flag on `app.credentials_issued`: the catalog
   * names past-tense outcomes, and recording a refusal under "issued" makes the
   * one query an incident asks — "was a token ever handed out for this account"
   * — answer wrongly.
   */
  'app.credentials_issuance_refused': 'configuration',
  'app.onboarding_config_updated': 'configuration',
  'app.pre_login_settings_updated': 'configuration',
  'app.branding_config_updated': 'configuration',
  'app.version_created': 'configuration',
  /**
   * The platform registered (or re-pointed) this app's App Store Connect webhook
   * (SCH-1294 R4). Payload: `{ webhookId, url, reused }` — never the secret. The
   * latest one of these is what readiness reads as "registered".
   */
  'webhook.registered': 'configuration',
  /**
   * A test ping sent through `POST /v1/webhookPings` was matched back to a
   * `webhookPingCreated` delivery for this app (SCH-1294 R6). Payload:
   * `{ webhookId, deliveryPublicId, receivedAt }`. Recorded only on a MATCH — a
   * ping that was sent and never arrived writes nothing, so this event is proof.
   */
  'webhook.ping_received': 'configuration',

  // --- asset: image slots (the history that exists nowhere else) ---
  'asset.slots_initialized': 'asset',
  'asset.committed': 'asset',
  /**
   * Store screenshots (SCH-1945). A screenshot is not a slot — it belongs to an
   * ordered set keyed by store, locale, version and device class — but it is the
   * same kind of history, so the four types share the `asset` class with
   * `asset.committed`.
   *
   * Payload: `{ store, versionString, deviceClass, position?, cloudinaryPublicId?,
   * fromVersion?, toVersion?, copied?, removed?, added?, source }`, where `source`
   * names the lane: `admin` for the super-admin panel, `internal` for the
   * API-key lane a script or an AI workflow uses.
   */
  'screenshot.committed': 'asset',
  'screenshot.removed': 'asset',
  'screenshot.set_copied': 'asset',
  'screenshot.set_replaced': 'asset',

  // --- onboarding: the provisioning orchestrator ---
  'provisioning.run_started': 'onboarding',
  'provisioning.step_succeeded': 'onboarding',
  'provisioning.step_failed': 'onboarding',
  'provisioning.step_retried': 'onboarding',
  'provisioning.step_manually_completed': 'onboarding',
  'provisioning.run_completed': 'onboarding',
  'provisioning.run_failed': 'onboarding',
  // Browser-dispatch lifecycle: a batch of portal steps handed to a Journey CLI
  // runner (a detached local child process), whose per-step outcomes come back
  // through the internal report endpoint.
  'provisioning.browser_dispatch_created': 'onboarding',
  'provisioning.browser_dispatch_step': 'onboarding',
  'provisioning.browser_dispatch_finished': 'onboarding',

  // --- push: OneSignal wiring ---
  'push.config_updated': 'push'
} as const satisfies Record<string, MobileAppEventClass>

export type MobileAppEventType = keyof typeof MOBILE_APP_EVENT_TYPES

export const MOBILE_APP_EVENT_TYPE_LIST = Object.keys(MOBILE_APP_EVENT_TYPES) as MobileAppEventType[]

export const MobileAppEventTypeSchema = z.enum(
  MOBILE_APP_EVENT_TYPE_LIST as [MobileAppEventType, ...MobileAppEventType[]]
)

export function isMobileAppEventType(value: string): value is MobileAppEventType {
  return value in MOBILE_APP_EVENT_TYPES
}

/** The class an event type belongs to. Never let a caller supply this itself. */
export function mobileAppEventClassFor(eventType: MobileAppEventType): MobileAppEventClass {
  return MOBILE_APP_EVENT_TYPES[eventType]
}

// =============================================================================
// Actor
// =============================================================================

/**
 * Actor classes. `agent` is distinct from `human` because R23 requires
 * agent-initiated actions to be attributable as such everywhere they surface;
 * `webhook` covers inbound provider callbacks (EAS, App Store Connect,
 * GitHub) and `system` covers reconcilers and scheduled jobs.
 */
export const MOBILE_APP_EVENT_ACTOR_TYPES = ['human', 'agent', 'webhook', 'system'] as const

export const MobileAppEventActorTypeSchema = z.enum(MOBILE_APP_EVENT_ACTOR_TYPES)
export type MobileAppEventActorType = z.infer<typeof MobileAppEventActorTypeSchema>

// =============================================================================
// Entity
// =============================================================================

export const MobileAppEventSchema = z.object({
  id: z.string(),
  /** MobileApp.publicId — denormalized on the row, so it reads even if the app is archived. */
  mobileAppId: z.string(),
  eventClass: MobileAppEventClassSchema,
  eventType: MobileAppEventTypeSchema,
  actorType: MobileAppEventActorTypeSchema,
  actorId: z.string().nullable(),
  actorLabel: z.string().nullable(),
  approverId: z.string().nullable(),
  summary: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  occurredAt: z.string().datetime(),
  createdAt: z.string().datetime()
})
export type MobileAppEvent = z.infer<typeof MobileAppEventSchema>

// =============================================================================
// List contract
// =============================================================================

export const MobileAppEventListSchema = z.object({
  events: z.array(MobileAppEventSchema),
  /**
   * Opaque cursor — the `id` of the last event in this page. Pass it back as
   * `cursor` for the next page. Null when the stream is exhausted.
   */
  nextCursor: z.string().nullable()
})
export type MobileAppEventList = z.infer<typeof MobileAppEventListSchema>

export const MOBILE_APP_EVENT_PAGE_SIZE_DEFAULT = 50
export const MOBILE_APP_EVENT_PAGE_SIZE_MAX = 200
